import React, { useContext } from 'react';
import { IntegrationContext } from '../../../../../context/IntegrationContext';
import { Company } from '../../../../../types/company';
import { CompanyRelationService } from '../../../../../types/integration_settings';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { Collapsable } from '../../../Collapsable';
import { FeatureToggleItem, FeatureToggleTitle, FeatureToggleExplanation, FeatureToggleInformation } from '../common';

interface TaskFeatureTogglesViewProps {
  company: Company;
  update: (company: Company) => Promise<void>;
}

export default function TaskFeatureTogglesView({ company, update }: TaskFeatureTogglesViewProps) {
  const { integrationSettings } = useContext(IntegrationContext);
  const hasSapIntegrationSetting = !![...integrationSettings.values()].find(
    is => is.type === CompanyRelationService.sap,
  );

  const copy = new Company(company);
  const toggles = copy.settings.featureToggles.tasks;

  return (
    <Collapsable
      open={toggles.tasks}
      header={
        <FeatureToggleItem
          value={toggles.tasks}
          onChange={async value => await update(copy.withTasksTaskFeatureToggle(value))}
        >
          <FeatureToggleTitle text={t(TranslationKey.taskTaskFeatureToggleTitle).singular.upper} />
          <FeatureToggleExplanation text={t(TranslationKey.taskTaskFeatureToggleExplanation).singular.upper} />
          <FeatureToggleInformation text={t(TranslationKey.taskTaskFeatureToggleInformation).singular.upper} />
        </FeatureToggleItem>
      }
    >
      <FeatureToggleItem
        indent={1}
        value={toggles.cycleCount}
        onChange={async value => await update(copy.withTasksCycleCountToggle(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.taskCycleCountFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation text={t(TranslationKey.taskCycleCountFeatureToggleExplanation).singular.upper} />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.blindCount}
        onChange={async value => await update(copy.withTasksBlindCountToggle(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.taskBlindCountFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation text={t(TranslationKey.taskBlindCountFeatureToggleExplanation).singular.upper} />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.quickCount}
        onChange={async value => await update(copy.withTaskQuickCountToggle(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.taskQuickCountFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation text={t(TranslationKey.taskQuickCountFeatureToggleExplanation).singular.upper} />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.allowStockUpdate}
        onChange={async value => await update(copy.withTasksStockUpdateToggle(value))}
      >
        <FeatureToggleTitle
          text={t(TranslationKey.taskAllowStockUpdateAfterFinishingTaskFeatureToggleTitle).singular.upper}
        />
        <FeatureToggleExplanation
          text={t(TranslationKey.taskAllowStockUpdateAfterFinishingTaskFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>

      {hasSapIntegrationSetting && (
        <FeatureToggleItem
          indent={1}
          value={copy.settings.featureToggles.sap.allowUncounted}
          onChange={async value => await update(copy.withSAPAllowUncountedFeatureToggle(value))}
        >
          <FeatureToggleTitle text={t(TranslationKey.taskAllowUncountedFeatureToggleTitle).singular.upper} />
          <FeatureToggleExplanation
            text={t(TranslationKey.taskAllowUncountedFeatureToggleExplanation).singular.upper}
          />
        </FeatureToggleItem>
      )}

      <FeatureToggleItem
        indent={1}
        value={toggles.taskOrders.taskOrders}
        onChange={async value => await update(copy.withTaskOrderToggle(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.taskOrderFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation text={t(TranslationKey.taskOrderFeatureToggleExplanation).singular.upper} />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={2}
        value={toggles.taskOrders.auto}
        onChange={async value => await update(copy.withTaskOrderAutoToggle(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.taskOrderAutoCreateFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation text={t(TranslationKey.taskOrderAutoCreateFeatureToggleExplanation).singular.upper} />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={2}
        value={toggles.taskOrders.refill.refill}
        onChange={async value => await update(copy.withTaskOrderRefillToggle(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.taskOrderRefillFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation text={t(TranslationKey.taskOrderRefillFeatureToggleExplanation).singular.upper} />
      </FeatureToggleItem>

      {toggles.taskOrders.taskOrders ? (
        !toggles.taskOrders.refill.refill ? (
          <>
            <FeatureToggleItem
              indent={2}
              value={toggles.taskOrders.inbound}
              onChange={async value => await update(copy.withTaskOrderInboundToggle(value))}
            >
              <FeatureToggleTitle text={t(TranslationKey.taskOrderInboundFeatureToggleTitle).singular.upper} />
              <FeatureToggleExplanation
                text={t(TranslationKey.taskOrderInboundFeatureToggleExplanation).singular.upper}
              />
              <FeatureToggleInformation
                text={t(TranslationKey.taskOrderInboundFeatureToggleInformation).singular.upper}
              />
            </FeatureToggleItem>

            <FeatureToggleItem
              indent={2}
              value={toggles.taskOrders.move}
              onChange={async value => await update(copy.withTaskOrderMoveToggle(value))}
            >
              <FeatureToggleTitle text={t(TranslationKey.taskOrderMoveFeatureToggleTitle).singular.upper} />
              <FeatureToggleExplanation text={t(TranslationKey.taskOrderMoveFeatureToggleExplanation).singular.upper} />
            </FeatureToggleItem>

            <FeatureToggleItem
              indent={2}
              value={toggles.taskOrders.outbound}
              onChange={async value => await update(copy.withTaskOrderOutboundToggle(value))}
            >
              <FeatureToggleTitle text={t(TranslationKey.taskOrderOutboundFeatureToggleTitle).singular.upper} />
              <FeatureToggleExplanation
                text={t(TranslationKey.taskOrderOutboundFeatureToggleExplanation).singular.upper}
              />
              <FeatureToggleInformation
                text={t(TranslationKey.taskOrderOutboundFeatureToggleInformation).singular.upper}
              />
            </FeatureToggleItem>
          </>
        ) : (
          <>
            <FeatureToggleItem
              indent={2}
              value={toggles.taskOrders.refill.ignoreOverstock}
              onChange={async value => await update(copy.witTaskOrderRefillIgnoreOverstockToggle(value))}
            >
              <FeatureToggleTitle
                text={t(TranslationKey.taskOrderRefillIgnoreOverstockFeatureToggleTitle).singular.upper}
              />
              <FeatureToggleExplanation
                text={t(TranslationKey.taskOrderRefillIgnoreOverstockFeatureToggleExplanation).singular.upper}
              />
            </FeatureToggleItem>
          </>
        )
      ) : null}
    </Collapsable>
  );
}
